import { Check } from "@phosphor-icons/react";
import { pillarStyle, normalizeSteps } from "@/lib/planiq";
import { stripMd } from "@/lib/stripMd";
import Logomark from "@/components/Logomark";

// A simulated A4 page sheet with a running header and footer (page numbers). It
// renders identically on screen (so the artifact can be reviewed page-by-page in the
// app) and in the exported/printed PDF.
export function PageSheet({ pageNum, total, title, landscape = false, breakClass = "", children }) {
  const today = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  return (
    <section className={`pq-sheet bg-white ${landscape ? "pdf-landscape" : ""} ${breakClass}`} data-testid={`page-sheet-${pageNum}`}>
      <div className="pq-header">
        <span className="inline-flex items-center gap-2 text-gray-950">
          <Logomark size={16} />
          <span className="font-display font-black text-sm tracking-tight">Xartis</span>
        </span>
        <span className="font-mono text-[10px] text-gray-400 uppercase tracking-wider truncate max-w-[50%]">{title}</span>
      </div>
      <div className="pq-body">{children}</div>
      <div className="pq-footer">
        <span className="font-mono text-[10px] text-gray-400">Created {today}</span>
        <span className="font-mono text-[10px] text-gray-400">Page {pageNum} of {total}</span>
      </div>
    </section>
  );
}

export function HorizonHeader({ h }) {
  return (
    <div className="border-b-2 border-gray-950 pb-2.5 mb-5">
      <h2 className="font-display font-bold text-2xl tracking-tight">{h.label}</h2>
      <p className="text-xs font-mono text-gray-600 mt-1">
        {h.posture} <span className="text-gray-300">·</span> <span className="uppercase tracking-[0.12em] text-gray-400">{h.sub}</span>
      </p>
    </div>
  );
}

export function CardBlock({ c, pillarById }) {
  const p = pillarById[c.pillar_id];
  const style = pillarStyle(p);
  const steps = normalizeSteps(c.steps);
  return (
    <div className="pq-hcard border-l-2 pl-3 avoid-break mb-5 break-inside-avoid" style={{ borderColor: style.hex }}>
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-semibold text-gray-950 text-[11.5pt] leading-tight">{stripMd(c.title)}</p>
        <span className="shrink-0 text-[6.5pt] font-mono uppercase tracking-wider px-1 py-0.5 border border-gray-300 text-gray-500">{p?.name || "Unassigned"}</span>
      </div>
      <p className="text-[10pt] text-gray-600 mt-1 mb-2 leading-[14pt]">{stripMd(c.justification)}</p>
      {steps.length > 0 && (
        <ul className="space-y-0.5">
          {steps.map((s, i) => (
            <li key={i} className="flex items-start gap-1.5 text-[10pt] leading-[14pt] text-gray-800">
              <span className={`mt-[2px] w-3 h-3 border shrink-0 flex items-center justify-center ${s.done ? "bg-gray-950 border-gray-950" : "border-gray-400 bg-white"}`}>
                {s.done && <Check size={8} weight="bold" className="text-white" />}
              </span>
              <span className={s.done ? "line-through text-gray-400" : ""}>{s.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
